import { Injectable } from '@angular/core';
import { SessionService } from '@galaxy/core';
import { BasePersona, IAMApiService, IAMService, PersonaType, UserIAMApiService } from '@vendasta/iam';
import { parseTokenInsecure } from '@vendasta/iamv2';
import jwtDecode from 'jwt-decode';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, switchMap, take } from 'rxjs/operators';

export enum IsPartner {
  No,
  Yes,
  Unauthorized,
}

@Injectable()
export class UserService {
  private isPartner$$: BehaviorSubject<IsPartner> = new BehaviorSubject<IsPartner>(IsPartner.Unauthorized);
  isPartner$: Observable<IsPartner> = this.isPartner$$.asObservable();

  private persona$$: BehaviorSubject<BasePersona> = new BehaviorSubject<BasePersona>(undefined);
  persona$: Observable<BasePersona> = this.persona$$.asObservable();

  readonly isSuperAdmin$: Observable<boolean> = this.sessionService.getSessionId().pipe(
    switchMap((sessionId) => {
      if (!sessionId) {
        return of(false);
      }
      return this.userIamApi.listPersonas({ session: sessionId, type: 'superadmin' }).pipe(
        map((resp) => (resp?.personas?.length ?? 0) > 0),
        catchError(() => of(false)),
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly impersonateeUserId$: Observable<string> = this.sessionService.getSessionId().pipe(
    map((session) => {
      if (!session) return '';
      try {
        return parseTokenInsecure(session)?.impersonateeUserId || '';
      } catch {
        return '';
      }
    }),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly isImpersonating$: Observable<boolean> = this.impersonateeUserId$.pipe(map((id) => !!id));

  constructor(
    private iamService: IAMService,
    private userIamApi: UserIAMApiService,
    private iamApi: IAMApiService,
    private sessionService: SessionService,
  ) {
    this.getLoggedInSubject(PersonaType.partner)
      .pipe(
        take(1),
        shareReplay({ bufferSize: 1, refCount: true }),
        map((result: BasePersona) => {
          this.persona$$.next(result);
          if (result) {
            return IsPartner.Yes;
          }
          return IsPartner.No;
        }),
        catchError((err) => {
          this.persona$$.next(undefined);
          if (!!err.status && err.status === 401) {
            return of(IsPartner.Unauthorized);
          }
          return of(IsPartner.No);
        }),
      )
      .subscribe((isPartner: IsPartner) => this.isPartner$$.next(isPartner));
  }

  hasPartnerStaffOnPartner$(partnerId$: Observable<string | null>): Observable<boolean> {
    return combineLatest([this.sessionService.getSessionId(), partnerId$]).pipe(
      switchMap(([sessionId, partnerId]) => {
        if (!sessionId || !partnerId) return of(false);
        const email = this.emailFromSession(sessionId);
        if (!email) return of(false);
        return this.listPartnerStaffByEmail(partnerId, email);
      }),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  impersonateeHasPartnerStaffOnPartner$(partnerId$: Observable<string | null>): Observable<boolean> {
    return combineLatest([this.impersonateeUserId$, partnerId$]).pipe(
      switchMap(([impUserId, partnerId]) => {
        if (!impUserId || !partnerId) return of(false);
        return this.iamApi.getMultiUsers({ userIdentifiers: [{ userId: impUserId }] }).pipe(
          map((resp) => resp?.users?.[0]?.user?.email || ''),
          switchMap((email) => (email ? this.listPartnerStaffByEmail(partnerId, email) : of(false))),
          catchError(() => of(false)),
        );
      }),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private listPartnerStaffByEmail(partnerId: string, email: string): Observable<boolean> {
    return this.iamApi
      .listUsers({
        namespace: partnerId,
        email,
        pageSize: 1,
        userFilter: { subjectTypes: [PersonaType.partner], searchTerms: [] },
      })
      .pipe(
        map((resp) => (resp?.users?.length ?? 0) > 0),
        catchError(() => of(false)),
      );
  }

  private emailFromSession(session: string): string {
    try {
      const decoded = jwtDecode<{ sub?: string }>(session);
      return decoded?.sub || '';
    } catch {
      return '';
    }
  }

  private getLoggedInSubject(personaType: PersonaType, partnerId?: string): Observable<BasePersona | null> {
    return this.sessionService
      .getSessionId()
      .pipe(switchMap((sessionId) => this.iamService.getSubjectBySession(sessionId, personaType, partnerId)));
  }
}
